import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { loginSchema, type AdminLoginResponse, type AdminMfaSetupResponse, type LoginInput } from '@koyomi/shared';
import { QRCodeSVG } from 'qrcode.react';
import { Button, Card, Input, MaskedInput } from '@koyomi/ui';
import { api, ApiError } from '../../lib/api.ts';
import { useAdminAuth } from '../../app/auth-store.ts';

type Step = { kind: 'password' } | { kind: 'mfa'; challengeToken: string } | { kind: 'enroll'; challengeToken: string; setup: AdminMfaSetupResponse } | { kind: 'recovery'; codes: string[] };

/** Login do Admin: senha → TOTP (ou enrolamento na primeira vez, com recovery codes exibidos uma única vez). */
export function LoginPage() {
  const { t } = useTranslation(['auth', 'common', 'admin']);
  const setSession = useAdminAuth((s) => s.setSession);
  const [step, setStep] = useState<Step>({ kind: 'password' });
  const [pending, setPending] = useState<AdminLoginResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const form = useForm<LoginInput>({ resolver: zodResolver(loginSchema), defaultValues: { email: '', password: '' } });

  const msg = (err: unknown) => (err instanceof ApiError ? t(`common:errors.${err.code}`, { defaultValue: err.message }) : t('common:errors.INTERNAL'));

  const onPassword = form.handleSubmit(async (values) => {
    setError(null);
    try {
      const res = await api<{ challengeToken: string; mfaEnrolled: boolean }>('/admin/auth/login', { body: values });
      if (res.mfaEnrolled) setStep({ kind: 'mfa', challengeToken: res.challengeToken });
      else {
        const setup = await api<AdminMfaSetupResponse>('/admin/auth/mfa/setup', { body: { challengeToken: res.challengeToken } });
        setStep({ kind: 'enroll', challengeToken: res.challengeToken, setup });
      }
    } catch (err) {
      setError(err instanceof ApiError && err.code === 'FORBIDDEN' ? t('auth:notSuperAdmin') : msg(err));
    }
  });

  const onCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (step.kind !== 'mfa' && step.kind !== 'enroll') return;
    setBusy(true);
    setError(null);
    try {
      const path = step.kind === 'mfa' ? '/admin/auth/mfa' : '/admin/auth/mfa/confirm';
      const res = await api<AdminLoginResponse>(path, { body: { challengeToken: step.challengeToken, code } });
      if (step.kind === 'enroll') {
        setPending(res);
        setStep({ kind: 'recovery', codes: step.setup.recoveryCodes });
      } else setSession(res);
    } catch (err) {
      setError(msg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="k-auth">
      <div className="k-auth__card">
        <div>
          <div className="k-auth__brand">{t('common:appName')}</div>
          <div className="k-section-label" style={{ marginTop: 8 }}>{t('admin:subtitle')}</div>
        </div>

        {step.kind === 'password' && (
          <form className="k-stack" onSubmit={onPassword} noValidate>
            <h1 className="k-panel-title">{t('auth:login.title')}</h1>
            <Input label={t('auth:login.email')} type="email" autoComplete="email" autoFocus {...form.register('email')} error={form.formState.errors.email && t('common:errors.VALIDATION_ERROR')} />
            <Input label={t('auth:login.password')} type="password" autoComplete="current-password" {...form.register('password')} error={form.formState.errors.password && t('common:errors.VALIDATION_ERROR')} />
            {error && <div className="k-field__error" role="alert">{error}</div>}
            <Button type="submit" variant="primary" size="lg" block loading={form.formState.isSubmitting}>{t('auth:login.submit')}</Button>
          </form>
        )}

        {(step.kind === 'mfa' || step.kind === 'enroll') && (
          <form className="k-stack" onSubmit={onCode}>
            <h1 className="k-panel-title">{step.kind === 'mfa' ? t('auth:mfa.title') : t('auth:mfa.enroll.title')}</h1>
            <p className="k-muted" style={{ margin: 0 }}>{step.kind === 'mfa' ? t('auth:mfa.subtitle') : t('auth:mfa.enroll.subtitle')}</p>
            {step.kind === 'enroll' && (
              <Card tone="soft">
                <div style={{ display: 'grid', placeItems: 'center', gap: 12 }}>
                  {/* QR gerado localmente: o segredo TOTP nunca sai do navegador. */}
                  <div style={{ borderRadius: 8, background: '#fff', padding: 6, lineHeight: 0 }} role="img" aria-label="QR code"><QRCodeSVG value={step.setup.otpauthUrl} size={180} level="M" /></div>
                  <div className="k-section-label">{t('auth:mfa.enroll.secret')}</div>
                  <code className="k-mono" style={{ userSelect: 'all' }}>{step.setup.secret}</code>
                </div>
              </Card>
            )}
            <MaskedInput mask="otp" label={t('auth:mfa.code')} autoFocus value={code} onChange={(e) => setCode(e.target.value)} />
            {error && <div className="k-field__error" role="alert">{error}</div>}
            <Button type="submit" variant="primary" size="lg" block loading={busy}>{step.kind === 'mfa' ? t('auth:mfa.submit') : t('auth:mfa.enroll.confirm')}</Button>
            {step.kind === 'mfa' && <span className="k-muted" style={{ textAlign: 'center', fontSize: 12 }}>{t('auth:mfa.recovery')}</span>}
            <Button type="button" variant="ghost" onClick={() => { setStep({ kind: 'password' }); setCode(''); setError(null); }}>{t('common:actions.back')}</Button>
          </form>
        )}

        {step.kind === 'recovery' && (
          <div className="k-stack">
            <h1 className="k-panel-title">{t('auth:mfa.enroll.recoveryTitle')}</h1>
            <p className="k-muted" style={{ margin: 0 }}>{t('auth:mfa.enroll.recoveryHint')}</p>
            <Card tone="soft">
              <div className="k-mono" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {step.codes.map((c) => <span key={c}>{c}</span>)}
              </div>
            </Card>
            <Button variant="primary" size="lg" block onClick={() => pending && setSession(pending)}>{t('auth:mfa.enroll.saved')}</Button>
          </div>
        )}
      </div>
    </div>
  );
}
